import type { Subscription } from '../types/payment.types';

const ACCESS_STATUSES = new Set(['active', 'trialing']);
const TERMINAL_STATUSES = new Set(['canceled', 'incomplete_expired']);
const CHECKOUT_RECOVERABLE_STATUSES = new Set(['incomplete']);

/**
 * Returns true when local subscription details should grant playback access.
 */
export const isSubscriptionActive = (subscription: Subscription | null | undefined): boolean => {
    if (!subscription?.PlanStatus || !ACCESS_STATUSES.has(subscription.PlanStatus.toLowerCase())) {
        return false;
    }

    if (subscription.PlanCurrentPeriodEnd == null) {
        return true;
    }

    const planEnd = new Date(subscription.PlanCurrentPeriodEnd);
    return Number.isNaN(planEnd.getTime()) ? false : planEnd > new Date();
};

/**
 * Uses the backend access decision when available and falls back during a backend-first rollout.
 */
export const subscriptionGrantsAccess = (subscription: Subscription | null | undefined): boolean => {
    if (typeof subscription?.HasAccess === 'boolean') {
        return subscription.HasAccess;
    }

    return isSubscriptionActive(subscription);
};

/**
 * Returns true while Stripe can still recover payment or charge the customer.
 */
export const isSubscriptionNonTerminal = (subscription: Subscription | null | undefined): boolean => {
    if (!subscription) {
        return false;
    }

    return !TERMINAL_STATUSES.has((subscription.PlanStatus ?? '').toLowerCase());
};

/**
 * Returns true when Stripe has an unfinished checkout that the customer can resume.
 */
export const isSubscriptionCheckoutRecoverable = (
    subscription: Subscription | null | undefined
): boolean => CHECKOUT_RECOVERABLE_STATUSES.has((subscription?.PlanStatus ?? '').toLowerCase());

/**
 * Returns true when an existing subscription should be managed instead of opening checkout.
 */
export const requiresSubscriptionManagement = (
    subscription: Subscription | null | undefined
): boolean => isSubscriptionNonTerminal(subscription) && !isSubscriptionCheckoutRecoverable(subscription);
